import React, { FC } from 'react';
import { Select, MenuItem, Checkbox, ListItemText, FormControl, InputLabel, Box } from '@mui/material';
import { SelectTag } from './types';

interface DeviceSelectorProps {
  tags: SelectTag[];
  selectedDevices: string[]; // Array of selected device IDs
  friendlyNameMap: Map<string, string>;
  setSelectedDevices: (devices: string[]) => void; // Function to update selected devices
}

const DeviceSelector: FC<DeviceSelectorProps> = ({ tags, selectedDevices, friendlyNameMap, setSelectedDevices }) => {
  const friendlyId = (id: string) => friendlyNameMap.get(id) ?? id;

  const handleTagToggle = (tag: SelectTag) => {
    const tagDeviceIds = tag.devices.map((device) => friendlyId(device.id));
    const allSelected = tagDeviceIds.every((id) => selectedDevices.includes(id));

    if (allSelected) {
      // Deselect all devices under this tag
      setSelectedDevices(selectedDevices.filter((id) => !tagDeviceIds.includes(id)));
    } else {
      // Select all devices under this tag
      setSelectedDevices([...selectedDevices, ...tagDeviceIds.filter((id) => !selectedDevices.includes(id))]);
    }
  };

  const handleDeviceToggle = (deviceId: string) => {
    if (selectedDevices.includes(deviceId)) {
      setSelectedDevices(selectedDevices.filter((id) => id !== deviceId));
    } else {
      setSelectedDevices([...selectedDevices, deviceId]);
    }
  };

  const isTagSelected = (tag: SelectTag) =>
    tag.devices.every((device) => selectedDevices.includes(friendlyId(device.id)));

  const isDeviceSelected = (deviceId: string) => selectedDevices.includes(friendlyId(deviceId));

  return (
    <FormControl sx={{ width: 'clamp(300px, 30vw, 35vw)' }} size="small">
      <InputLabel id="device-selector-label">Select Devices</InputLabel>
      <Select
        multiple
        value={selectedDevices}
        renderValue={(selected) => selected.join(', ')}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300, // Set max height for the dropdown
              overflowY: 'auto', // Add scroll for overflow
            },
          },
        }}
        labelId="device-selector-label"
        label="Select Devices"
      >
        {tags.map((tag, idx) => (
          <Box key={tag.id} sx={{ backgroundColor: idx % 2 === 0 ? 'rgba(12, 92, 248, 0.1)' : 'unset', m: 0, p: 0 }}>
            <MenuItem onClick={() => handleTagToggle(tag)} style={{ fontWeight: 'bold' }}>
              <Checkbox checked={isTagSelected(tag)} />
              <ListItemText primary={tag.name} />
            </MenuItem>
            {tag.devices.map((device) => (
              <MenuItem
                key={device.id}
                onClick={() => handleDeviceToggle(friendlyId(device.id))}
                style={{ paddingLeft: '2em' }}
              >
                <Checkbox checked={isDeviceSelected(device.id)} />
                <ListItemText primary={device.name} />
              </MenuItem>
            ))}
          </Box>
        ))}
      </Select>
    </FormControl>
  );
};

export default DeviceSelector;
